import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGetSafeDepositBoxServiceRoleOptions } from '../../../../hooks/queries/privateBankingServiceRoles';
import { humanReadableString } from '../../../../utils/stringUtils';
import { ServiceAgreementV2 } from '../../eBankingTempTypes';
import { isSafeDepositBoxServiceAgreementProperties } from '../../productSpecificProperties';
import { ProductDetailRows } from './ProductDetailRows';

type Props = { product: ServiceAgreementV2 };

const NOT_AVAILABLE = '-';

type LabelledOption = { value: string; label: string };

/** Labels are server-owned; humanReadableString is only a fallback while the options are unavailable. */
const resolveLabel = (value: string | undefined, options: LabelledOption[]) => {
  if (!value) return NOT_AVAILABLE;
  return options.find((option) => option.value === value)?.label ?? humanReadableString(value) ?? NOT_AVAILABLE;
};

export const SafeDepositBoxConfigurationBlock: React.FC<Props> = ({ product }) => {
  const { t } = useTranslation();
  const optionsQuery = useGetSafeDepositBoxServiceRoleOptions(product.serviceAgreementId);
  const properties = product.productSpecificProperties;
  const details = isSafeDepositBoxServiceAgreementProperties(properties) ? properties : undefined;

  const rows = [
    {
      label: t('pb.productdetails.safedepositbox.safelocation.label'),
      value: resolveLabel(details?.safeLocation, optionsQuery.data?.safeLocations ?? []),
      testId: 'safe-deposit-box-detail-safe-location',
    },
    {
      label: t('pb.productdetails.safedepositbox.safenumber.label'),
      value: details?.safeNr?.trim() || NOT_AVAILABLE,
      testId: 'safe-deposit-box-detail-safe-number',
    },
    {
      label: t('pb.productdetails.safedepositbox.safesize.label'),
      value: resolveLabel(details?.safeSize, optionsQuery.data?.safeSizes ?? []),
      testId: 'safe-deposit-box-detail-safe-size',
    },
    {
      label: t('pb.productdetails.safedepositbox.keydeposit.label'),
      value: details
        ? t(
            details.keyDeposit
              ? 'pb.productdetails.safedepositbox.keydeposit.yes'
              : 'pb.productdetails.safedepositbox.keydeposit.no',
          )
        : NOT_AVAILABLE,
      testId: 'safe-deposit-box-detail-key-deposit',
    },
  ];

  return (
    <div data-testid="safe-deposit-box-configuration-block">
      <ProductDetailRows rows={rows} />
    </div>
  );
};
